import React, { useState } from "react";
import { useQuery } from "wasp/client/operations";
import { getScrapedRecipes, approveScrapedRecipe, updateScrapedRecipe, rejectScrapedRecipe, flagScrapedRecipe } from "wasp/client/operations";

export default function ScrapedRecipesPage() {
  const [selectedRecipe, setSelectedRecipe] = useState<any>(null);
  const [editMode, setEditMode] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("pending");

  const { data, isLoading, error, refetch } = useQuery(getScrapedRecipes, {
    status: statusFilter === "all" ? undefined : statusFilter,
    limit: 50,
    offset: 0
  });

  const handleApprove = async (id: string) => {
    try {
      await approveScrapedRecipe({ id });
      alert("Recipe approved and published!");
      refetch();
      setSelectedRecipe(null);
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleReject = async (id: string) => {
    if (!confirm("Are you sure you want to reject and delete this recipe?")) return;
    try {
      await rejectScrapedRecipe({ id });
      alert("Recipe rejected and deleted");
      refetch();
      setSelectedRecipe(null);
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleFlag = async (id: string, notes: string) => {
    try {
      await flagScrapedRecipe({ id, notes });
      alert("Recipe flagged for later review");
      refetch();
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    }
  };

  const handleUpdate = async (id: string, updates: any) => {
    try {
      await updateScrapedRecipe({ id, updates });
      alert("Recipe updated");
      refetch();
      setEditMode(false);
    } catch (err: any) {
      alert(`Error: ${err.message}`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading scraped recipes...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-red-500">Error: {error.message}</div>
      </div>
    );
  }

  const recipes = data?.recipes || [];
  const total = data?.total || 0;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-4">Scraped Recipes Review</h1>
        <div className="flex gap-4 mb-4">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border rounded"
          >
            <option value="all">All</option>
            <option value="pending">Pending</option>
            <option value="needs_edit">Needs Edit</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
          <div className="text-sm text-gray-600 flex items-center">
            Total: {total} recipes
          </div>
        </div>
      </div>

      {recipes.length === 0 ? (
        <div className="text-center text-gray-500 py-12">
          No recipes found with status: {statusFilter}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Recipe List */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold mb-4">
              Recipes ({recipes.length})
            </h2>
            <div className="space-y-2">
              {recipes.map((scraped) => (
                <div
                  key={scraped.id}
                  onClick={() => setSelectedRecipe(scraped)}
                  className={`p-4 border rounded cursor-pointer hover:bg-gray-50 ${
                    selectedRecipe?.id === scraped.id ? "border-blue-500 bg-blue-50" : ""
                  } ${scraped.needsReview ? "border-l-4 border-l-yellow-500" : ""}`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="font-semibold">{scraped.recipe.title || "Untitled"}</h3>
                      <div className="text-sm text-gray-600 mt-1">
                        <span className="inline-block mr-2">
                          Source: {scraped.source}
                        </span>
                        {scraped.needsReview && (
                          <span className="inline-block bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded text-xs">
                            AI Extracted
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Scraped: {new Date(scraped.scrapedAt).toLocaleString()}
                      </div>
                    </div>
                    <span
                      className={`text-xs px-2 py-1 rounded ${
                        scraped.status === "pending"
                          ? "bg-gray-100 text-gray-800"
                          : scraped.status === "approved"
                          ? "bg-green-100 text-green-800"
                          : scraped.status === "needs_edit"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {scraped.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Recipe Detail & Preview */}
          <div className="sticky top-4">
            {selectedRecipe ? (
              <div className="border rounded-lg p-6 space-y-4">
                <div className="flex justify-between items-start mb-4">
                  <h2 className="text-2xl font-bold">{selectedRecipe.recipe.title}</h2>
                  <button
                    onClick={() => setSelectedRecipe(null)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ✕
                  </button>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() => setEditMode(!editMode)}
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                  >
                    {editMode ? "Cancel Edit" : "Edit"}
                  </button>
                  <button
                    onClick={() => handleApprove(selectedRecipe.id)}
                    className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                    disabled={selectedRecipe.status === "approved"}
                  >
                    Approve & Publish
                  </button>
                  <button
                    onClick={() => handleFlag(selectedRecipe.id, "Needs review")}
                    className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600"
                  >
                    Flag for Later
                  </button>
                  <button
                    onClick={() => handleReject(selectedRecipe.id)}
                    className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
                  >
                    Reject & Delete
                  </button>
                </div>

                {/* Recipe Details */}
                {editMode ? (
                  <RecipeEditor
                    recipe={selectedRecipe.recipe}
                    onSave={(updates) => handleUpdate(selectedRecipe.id, updates)}
                    onCancel={() => setEditMode(false)}
                  />
                ) : (
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-semibold">Description</h3>
                      <p className="text-gray-700">{selectedRecipe.recipe.description || "No description"}</p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <h3 className="font-semibold">Prep Time</h3>
                        <p>{selectedRecipe.recipe.prepTime ? `${selectedRecipe.recipe.prepTime} min` : "N/A"}</p>
                      </div>
                      <div>
                        <h3 className="font-semibold">Servings</h3>
                        <p>{selectedRecipe.recipe.servings || "N/A"}</p>
                      </div>
                    </div>

                    <div>
                      <h3 className="font-semibold">Ingredients ({selectedRecipe.recipe.ingredients?.length || 0})</h3>
                      <ul className="list-disc list-inside mt-2 space-y-1">
                        {(selectedRecipe.recipe.ingredients || []).map((ing: any, i: number) => (
                          <li key={i} className="text-gray-700">
                            {typeof ing === "string" ? ing : ing.name}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h3 className="font-semibold">Instructions ({selectedRecipe.recipe.instructions?.length || 0})</h3>
                      <ol className="list-decimal list-inside mt-2 space-y-2">
                        {(selectedRecipe.recipe.instructions || []).map((step: any, i: number) => (
                          <li key={i} className="text-gray-700">
                            {typeof step === "string" ? step : step.instruction}
                          </li>
                        ))}
                      </ol>
                    </div>

                    {/* Source Preview */}
                    {selectedRecipe.sourceUrl && (
                      <div>
                        <h3 className="font-semibold mb-2">Source Preview</h3>
                        <iframe
                          src={selectedRecipe.sourceUrl}
                          className="w-full h-96 border rounded"
                          title="Source Preview"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="border rounded-lg p-12 text-center text-gray-500">
                Select a recipe to review
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Recipe Editor Component
function RecipeEditor({
  recipe,
  onSave,
  onCancel
}: {
  recipe: any;
  onSave: (updates: any) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState(recipe.title || "");
  const [description, setDescription] = useState(recipe.description || "");
  const [prepTime, setPrepTime] = useState(recipe.prepTime || "");
  const [servings, setServings] = useState(recipe.servings || "");

  const handleSave = () => {
    onSave({
      title,
      description,
      prepTime: parseInt(prepTime) || null,
      servings: parseInt(servings) || null
    });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block font-semibold mb-1">Title</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="w-full px-3 py-2 border rounded"
        />
      </div>

      <div>
        <label className="block font-semibold mb-1">Description</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full px-3 py-2 border rounded"
          rows={3}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block font-semibold mb-1">Prep Time (min)</label>
          <input
            type="number"
            value={prepTime}
            onChange={(e) => setPrepTime(e.target.value)}
            className="w-full px-3 py-2 border rounded"
          />
        </div>
        <div>
          <label className="block font-semibold mb-1">Servings</label>
          <input
            type="number"
            value={servings}
            onChange={(e) => setServings(e.target.value)}
            className="w-full px-3 py-2 border rounded"
          />
        </div>
      </div>

      <div className="flex gap-2">
        <button
          onClick={handleSave}
          className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
        >
          Save Changes
        </button>
        <button
          onClick={onCancel}
          className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
